import React, { useEffect, useState } from 'react';
import useScreen from 'src/hooks/useScreen';
import queryString from 'query-string';
import { useNavigate } from 'react-router-dom';
import PermissionsProvider from 'src/providers/PermissionsProvider';
import useBreadcrumb from 'src/hooks/useBreadcrumb';
import useTheme from 'src/hooks/useTheme';
import useUserInfo from 'src/hooks/useUserInfo';
import useAsyncProcesses from 'src/hooks/useAsyncProcesses';
import TitleBar from 'src/components/layout/TitleBar';
import { LuBuilding } from 'react-icons/lu';
import { Space, Table, Tooltip, Switch, Input, Spin, Dropdown, notification } from 'antd';
import { searchTenants, postTenant } from 'src/api/tenants';
import Splitter, { SplitDirection } from '@devbookhq/splitter';
import TenantEditor from './Components/TenantEditor';
import { FaStar, FaPlus } from 'react-icons/fa6';
import StyledButton from 'src/components/layout/StyledButton';
import { notEmptyArray } from 'src/misc/Misc';
import TenantCreate from './Components/TenantCreate';
import { SearchOutlined } from '@ant-design/icons';
import WhenDesktop from 'src/components/layout/WhenDesktop';

//import PropTypes from 'prop-types';

var tenantsCurrentPage = 1;

const MasterAdminTenants = () => {
  const { tenantId } = queryString.parse(window.location.search);

  const navigate = useNavigate();
  const { setBreadcrumb, dropBreadcrumb } = useBreadcrumb();
  const { executeAsyncProcess } = useAsyncProcesses();
  const { theme } = useTheme();
  const { tenant_id } = useUserInfo();
  const { isDesktop } = useScreen();

  const items = [
    {
      key: 10,
      label: <>10</>,
    },
    {
      key: 20,
      label: <>20</>,
    },
    {
      key: 30,
      label: <>30</>,
    },
  ];

  const columns = [
    {
      title: 'ID',
      key: 'id',
      width: 360,
      render: (record) => <>{record.id}</>,
    },
    {
      title: 'Name',
      key: 'name',
      render: (record) => (
        <Space className='btn'>
          {record.name}
          {record.id === tenant_id && (
            <Tooltip title={"Current user's tenant"}>
              <FaStar className='text-yellow-500' />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const [tenants, setTenants] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [layoutHorizontal, setLayoutHorizontal] = useState(isDesktop);
  const [newTenantOpen, setNewTenantOpen] = useState(false);
  const [query, setQuery] = useState(tenantId ?? null);
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(parseInt(items[0].key));
  const [thereIsMore, setThereIsMore] = useState(false);
  const [rerenderTable, setRerenderTable] = useState(false);

  useEffect(() => {
    setBreadcrumb([
      {
        title: 'Master Admin',
        path: '/master-admin',
      },
      {
        title: 'Tenants',
      },
    ]);

    return () => {
      dropBreadcrumb();
    };
  }, []);

  useEffect(() => {
    tenantsCurrentPage = 1;
    setRerenderTable(true);
    setTenants(null);
    setThereIsMore(false);
    setSelectedTenant(null);
    loadData();
  }, [query, pageSize]);

  useEffect(() => {
    if (rerenderTable) setRerenderTable(false);
  }, [rerenderTable]);

  const loadData = async (loadMore = false) => {
    setLoading(true);
    try {
      const q = query?.trim() ?? null;
      const resp = await searchTenants(q?.length > 0 ? q : null, tenantsCurrentPage, pageSize);
      tenantsCurrentPage++;
      setThereIsMore(resp?.length === pageSize ? true : false);
      if (true === notEmptyArray(resp)) {
        if (loadMore) setTenants([...tenants, ...resp]);
        else setTenants(resp);
      }
      if (tenantId) {
        setSelectedTenant(tenantId);
      }
    } catch (error) {
      if (error?.toString().includes('The user aborted a request')) return;
      console.log(error);
    }
    setLoading(false);
  };

  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      console.log(`selectedRowKeys: ${selectedRowKeys}`, 'selectedRows: ', selectedRows);
      setSelectedTenant(selectedRowKeys[0] ?? null);
    },
    selectedRowKeys: [selectedTenant],
    type: 'radio',
  };

  const handleOnRow = (record) => {
    return {
      onClick: () => {
        setSelectedTenant(record.id);
      },
    };
  };

  const handleSelectedTenantOnSave = (tenant) => {
    const index = tenants.findIndex((o) => o.id === tenant.id);
    if (index < 0) return;
    const newTenants = [...tenants];
    newTenants[index] = tenant;
    setTenants(newTenants);
  };

  const handleTenantCreateOnSubmit = (values) => {
    console.log('handleTenantCreateOnSubmit', values);
    setNewTenantOpen(false);
    executeAsyncProcess(async () => {
      try {
        const resp = await postTenant(values);
        const [...o] = true === notEmptyArray(tenants) ? tenants : [];
        o.unshift(resp);
        setTenants(o);
        notification.success({
          message: 'Success',
          description: <>New tenant has been created</>,
          duration: 5,
        });
      } catch (error) {
        console.log(error);
      }
    });
  };

  return (
    <PermissionsProvider requiredPermissions={['admin.master']}>
      <TenantCreate open={newTenantOpen} onClose={() => setNewTenantOpen(false)} onSubmit={handleTenantCreateOnSubmit} />
      <TitleBar
        title={
          <Space className='btn'>
            <LuBuilding />
            Tenants
          </Space>
        }
        afterTitleExtras={
          <StyledButton color={theme.textBase} onClick={() => setNewTenantOpen(true)} size='small'>
            <Space className='btn'>
              <FaPlus />
              <WhenDesktop>Create tenant</WhenDesktop>
            </Space>
          </StyledButton>
        }
      >
        <Space>
          <Switch
            unCheckedChildren={<>vertical</>}
            checkedChildren={<>horizontal</>}
            checked={layoutHorizontal}
            onChange={() => {
              setLayoutHorizontal(!layoutHorizontal);
            }}
          />
        </Space>
      </TitleBar>
      <div className='p-4 h-full w-full'>
        <Splitter direction={layoutHorizontal ? SplitDirection.Horizontal : SplitDirection.Vertical}>
          <div className={'overflow-scroll h-full w-full p-4'} style={{ backgroundColor: theme.backgroundBase }}>
            <div className='flex-initial p-4 flex flex-row justify-center'>
              <div className='flex flex-col'>
                <Input
                  disabled={tenantId && loading}
                  className='w-60'
                  placeholder='Search...'
                  prefix={loading ? <Spin size='small' className='mr-2' /> : <SearchOutlined className='mr-2' />}
                  allowClear
                  value={query}
                  onChange={(e) => {
                    if (tenantId) navigate('/master-admin/tenants');
                    setQuery(e.target.value);
                  }}
                />

                {!tenantId && (
                  <div>
                    <Dropdown
                      trigger={['click']}
                      menu={{
                        activeKey: pageSize,
                        items,
                        onClick: (e) => {
                          console.log(e);
                          setPageSize(parseInt(e.key));
                        },
                      }}
                      className='text-xs cursor-pointer'
                    >
                      <span className='text-blue-600 opacity-40 hover:opacity-100 '>page size {pageSize}</span>
                    </Dropdown>
                  </div>
                )}
              </div>
            </div>
            <div className='flex-auto overflow-auto'>
              {!rerenderTable && (
                <Table
                  loading={thereIsMore && loading}
                  pagination={{
                    pageSize,
                    total: thereIsMore ? tenants?.length + 1 : tenants?.length ?? 0,
                    onChange: (page, pageSize) => (thereIsMore && page > tenants?.length / pageSize ? loadData(true) : null),
                  }}
                  size='small'
                  columns={columns}
                  dataSource={tenants}
                  rowKey={'id'}
                  rowSelection={rowSelection}
                  onRow={handleOnRow}
                />
              )}
            </div>
          </div>
          <div className={'overflow-scroll h-full w-full'} style={{ backgroundColor: theme.backgroundBase }}>
            <TenantEditor tenantId={selectedTenant} onSave={handleSelectedTenantOnSave} />
          </div>
        </Splitter>
      </div>
    </PermissionsProvider>
  );
};

// MasterAdminTenants.propTypes = {};

export default MasterAdminTenants;
